/**
 * OKR Store
 *
 * Manages Objectives & Key Results for the workspace.
 * Supabase is the source of truth; Zustand is a client cache.
 */

import { create } from 'zustand';
import {
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
  generateUUID,
} from '../../lib/supabaseSync';
import type {
  OKR,
  OKRKeyResult,
  OKRCheckIn,
  OKRStatus,
  KeyResultStatus,
  KeyResultConfidence,
  KeyResultMetricType,
  OKRTimePeriod,
} from '../../types/okr';

// ── DB row → domain type mappers ──────────────────────────

function rowToOKR(row: Record<string, unknown>): OKR {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    parentOkrId: (row.parent_okr_id as string | null) ?? null,
    title: row.title as string,
    description: (row.description as string | null) ?? null,
    ownerUserId: (row.owner_user_id as string | null) ?? null,
    ownerOrgUnitId: (row.owner_org_unit_id as string | null) ?? null,
    timePeriod: (row.time_period as OKRTimePeriod) ?? 'quarterly',
    periodLabel: (row.period_label as string | null) ?? null,
    startDate: (row.start_date as string | null) ?? null,
    endDate: (row.end_date as string | null) ?? null,
    status: (row.status as OKRStatus) ?? 'draft',
    confidenceScore: (row.confidence_score as number | null) ?? null,
    strategicObjectiveId: (row.strategic_objective_id as string | null) ?? null,
    tags: (row.tags as string[]) ?? [],
    sortOrder: (row.sort_order as number) ?? 0,
    createdBy: (row.created_by as string | null) ?? null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

function rowToKeyResult(row: Record<string, unknown>): OKRKeyResult {
  return {
    id: row.id as string,
    okrId: row.okr_id as string,
    workspaceId: row.workspace_id as string,
    title: row.title as string,
    description: (row.description as string | null) ?? null,
    metricType: (row.metric_type as KeyResultMetricType) ?? 'percentage',
    metricUnit: (row.metric_unit as string | null) ?? null,
    startValue: (row.start_value as number | null) ?? null,
    targetValue: (row.target_value as number) ?? 100,
    currentValue: (row.current_value as number) ?? 0,
    status: (row.status as KeyResultStatus) ?? 'not_started',
    confidence: (row.confidence as KeyResultConfidence) ?? 'medium',
    sortOrder: (row.sort_order as number) ?? 0,
    createdBy: (row.created_by as string | null) ?? null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

function rowToCheckIn(row: Record<string, unknown>): OKRCheckIn {
  return {
    id: row.id as string,
    keyResultId: row.key_result_id as string,
    workspaceId: row.workspace_id as string,
    value: row.value as number,
    note: (row.note as string | null) ?? null,
    checkedInBy: (row.checked_in_by as string | null) ?? null,
    checkedInAt: row.checked_in_at as string,
  };
}

// ── Store types ───────────────────────────────────────────

export interface CreateOKRInput {
  title: string;
  description?: string;
  parentOkrId?: string;
  timePeriod?: OKRTimePeriod;
  periodLabel?: string;
  startDate?: string;
  endDate?: string;
  status?: OKRStatus;
  tags?: string[];
}

export interface UpdateOKRInput {
  title?: string;
  description?: string;
  timePeriod?: OKRTimePeriod;
  periodLabel?: string;
  startDate?: string;
  endDate?: string;
  status?: OKRStatus;
  confidenceScore?: number | null;
  strategicObjectiveId?: string | null;
  tags?: string[];
  sortOrder?: number;
}

export interface CreateKeyResultInput {
  title: string;
  description?: string;
  metricType?: KeyResultMetricType;
  metricUnit?: string;
  startValue?: number;
  targetValue?: number;
  currentValue?: number;
  status?: KeyResultStatus;
  confidence?: KeyResultConfidence;
}

export interface UpdateKeyResultInput {
  title?: string;
  description?: string;
  metricType?: KeyResultMetricType;
  metricUnit?: string;
  startValue?: number | null;
  targetValue?: number;
  currentValue?: number;
  status?: KeyResultStatus;
  confidence?: KeyResultConfidence;
  sortOrder?: number;
}

interface OKRState {
  okrs: OKR[];
  keyResults: OKRKeyResult[];
  checkIns: OKRCheckIn[];
  isLoading: boolean;
  error: string | null;

  // Fetch
  fetchOKRs: () => Promise<void>;
  fetchKeyResults: (okrId: string) => Promise<void>;
  fetchCheckIns: (keyResultId: string) => Promise<void>;

  // OKR CRUD
  createOKR: (input: CreateOKRInput) => Promise<{ okr: OKR | null; error?: string }>;
  updateOKR: (id: string, updates: UpdateOKRInput) => Promise<{ success: boolean; error?: string }>;
  deleteOKR: (id: string) => Promise<{ success: boolean; error?: string }>;

  // Key Result CRUD
  createKeyResult: (okrId: string, input: CreateKeyResultInput) => Promise<{ keyResult: OKRKeyResult | null; error?: string }>;
  updateKeyResult: (id: string, updates: UpdateKeyResultInput) => Promise<{ success: boolean; error?: string }>;
  deleteKeyResult: (id: string) => Promise<{ success: boolean; error?: string }>;

  // Check-in
  addCheckIn: (keyResultId: string, value: number, note?: string) => Promise<{ checkIn: OKRCheckIn | null; error?: string }>;

  // Selectors
  getOKRById: (id: string) => OKR | undefined;
  getTopLevelOKRs: () => OKR[];
  getChildOKRs: (parentId: string) => OKR[];
  getKeyResultsForOKR: (okrId: string) => OKRKeyResult[];
  getCheckInsForKeyResult: (keyResultId: string) => OKRCheckIn[];
  getOKRWithKeyResults: (id: string) => OKR | undefined;
}

// ── Store implementation ──────────────────────────────────

export const useOKRStore = create<OKRState>((set, get) => ({
  okrs: [],
  keyResults: [],
  checkIns: [],
  isLoading: false,
  error: null,

  // ── Fetch ────────────────────────────────────────────────

  fetchOKRs: async () => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return;

    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('okrs')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) throw error;
      set({ okrs: (data ?? []).map(rowToOKR), isLoading: false });
    } catch (err) {
      set({ error: String(err), isLoading: false });
    }
  },

  fetchKeyResults: async (okrId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    const { data, error } = await supabase
      .from('okr_key_results')
      .select('*')
      .eq('okr_id', okrId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });

    if (error) return;
    const fetched = (data ?? []).map(rowToKeyResult);
    set((s) => ({
      keyResults: [
        ...s.keyResults.filter((kr) => kr.okrId !== okrId),
        ...fetched,
      ],
    }));
  },

  fetchCheckIns: async (keyResultId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    const { data, error } = await supabase
      .from('okr_check_ins')
      .select('*')
      .eq('key_result_id', keyResultId)
      .order('checked_in_at', { ascending: false });

    if (error) return;
    const fetched = (data ?? []).map(rowToCheckIn);
    set((s) => ({
      checkIns: [
        ...s.checkIns.filter((ci) => ci.keyResultId !== keyResultId),
        ...fetched,
      ],
    }));
  },

  // ── OKR CRUD ─────────────────────────────────────────────

  createOKR: async (input) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return { okr: null, error: 'Supabase unavailable' };

    const id = generateUUID();
    const row = {
      id,
      workspace_id: workspaceId,
      parent_okr_id: input.parentOkrId ?? null,
      title: input.title,
      description: input.description ?? null,
      time_period: input.timePeriod ?? 'quarterly',
      period_label: input.periodLabel ?? null,
      start_date: input.startDate ?? null,
      end_date: input.endDate ?? null,
      status: input.status ?? 'draft',
      tags: input.tags ?? [],
      sort_order: get().okrs.length,
    };

    const { data, error } = await supabase.from('okrs').insert(row).select().single();
    if (error) return { okr: null, error: error.message };

    const okr = rowToOKR(data as Record<string, unknown>);
    set((s) => ({ okrs: [...s.okrs, okr] }));
    return { okr };
  },

  updateOKR: async (id, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'Supabase unavailable' };

    const patch: Record<string, unknown> = {};
    if (updates.title !== undefined)                patch.title = updates.title;
    if (updates.description !== undefined)           patch.description = updates.description;
    if (updates.timePeriod !== undefined)            patch.time_period = updates.timePeriod;
    if (updates.periodLabel !== undefined)           patch.period_label = updates.periodLabel;
    if (updates.startDate !== undefined)             patch.start_date = updates.startDate;
    if (updates.endDate !== undefined)               patch.end_date = updates.endDate;
    if (updates.status !== undefined)                patch.status = updates.status;
    if (updates.confidenceScore !== undefined)       patch.confidence_score = updates.confidenceScore;
    if (updates.strategicObjectiveId !== undefined)  patch.strategic_objective_id = updates.strategicObjectiveId;
    if (updates.tags !== undefined)                  patch.tags = updates.tags;
    if (updates.sortOrder !== undefined)             patch.sort_order = updates.sortOrder;

    const { error } = await supabase.from('okrs').update(patch).eq('id', id);
    if (error) return { success: false, error: error.message };

    set((s) => ({
      okrs: s.okrs.map((o) => (o.id === id ? { ...o, ...updates } : o)),
    }));
    return { success: true };
  },

  deleteOKR: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'Supabase unavailable' };

    const { error } = await supabase.from('okrs').delete().eq('id', id);
    if (error) return { success: false, error: error.message };

    set((s) => ({
      okrs: s.okrs.filter((o) => o.id !== id),
      keyResults: s.keyResults.filter((kr) => kr.okrId !== id),
    }));
    return { success: true };
  },

  // ── Key Result CRUD ───────────────────────────────────────

  createKeyResult: async (okrId, input) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return { keyResult: null, error: 'Supabase unavailable' };

    const existing = get().keyResults.filter((kr) => kr.okrId === okrId);
    const row = {
      id: generateUUID(),
      okr_id: okrId,
      workspace_id: workspaceId,
      title: input.title,
      description: input.description ?? null,
      metric_type: input.metricType ?? 'percentage',
      metric_unit: input.metricUnit ?? null,
      start_value: input.startValue ?? null,
      target_value: input.targetValue ?? 100,
      current_value: input.currentValue ?? 0,
      status: input.status ?? 'not_started',
      confidence: input.confidence ?? 'medium',
      sort_order: existing.length,
    };

    const { data, error } = await supabase.from('okr_key_results').insert(row).select().single();
    if (error) return { keyResult: null, error: error.message };

    const keyResult = rowToKeyResult(data as Record<string, unknown>);
    set((s) => ({ keyResults: [...s.keyResults, keyResult] }));
    return { keyResult };
  },

  updateKeyResult: async (id, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'Supabase unavailable' };

    const patch: Record<string, unknown> = {};
    if (updates.title !== undefined)       patch.title = updates.title;
    if (updates.description !== undefined)  patch.description = updates.description;
    if (updates.metricType !== undefined)   patch.metric_type = updates.metricType;
    if (updates.metricUnit !== undefined)   patch.metric_unit = updates.metricUnit;
    if (updates.startValue !== undefined)   patch.start_value = updates.startValue;
    if (updates.targetValue !== undefined)  patch.target_value = updates.targetValue;
    if (updates.currentValue !== undefined) patch.current_value = updates.currentValue;
    if (updates.status !== undefined)       patch.status = updates.status;
    if (updates.confidence !== undefined)   patch.confidence = updates.confidence;
    if (updates.sortOrder !== undefined)    patch.sort_order = updates.sortOrder;

    const { error } = await supabase.from('okr_key_results').update(patch).eq('id', id);
    if (error) return { success: false, error: error.message };

    set((s) => ({
      keyResults: s.keyResults.map((kr) => (kr.id === id ? { ...kr, ...updates } : kr)),
    }));
    return { success: true };
  },

  deleteKeyResult: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'Supabase unavailable' };

    const { error } = await supabase.from('okr_key_results').delete().eq('id', id);
    if (error) return { success: false, error: error.message };

    set((s) => ({
      keyResults: s.keyResults.filter((kr) => kr.id !== id),
      checkIns: s.checkIns.filter((ci) => ci.keyResultId !== id),
    }));
    return { success: true };
  },

  // ── Check-in ──────────────────────────────────────────────

  addCheckIn: async (keyResultId, value, note) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return { checkIn: null, error: 'Supabase unavailable' };

    const row = {
      id: generateUUID(),
      key_result_id: keyResultId,
      workspace_id: workspaceId,
      value,
      note: note ?? null,
    };

    const { data, error } = await supabase.from('okr_check_ins').insert(row).select().single();
    if (error) return { checkIn: null, error: error.message };

    const checkIn = rowToCheckIn(data as Record<string, unknown>);

    // Also update the current_value on the key result
    await get().updateKeyResult(keyResultId, { currentValue: value });

    set((s) => ({ checkIns: [checkIn, ...s.checkIns] }));
    return { checkIn };
  },

  // ── Selectors ─────────────────────────────────────────────

  getOKRById: (id) => get().okrs.find((o) => o.id === id),

  getTopLevelOKRs: () => get().okrs.filter((o) => o.parentOkrId === null),

  getChildOKRs: (parentId) => get().okrs.filter((o) => o.parentOkrId === parentId),

  getKeyResultsForOKR: (okrId) =>
    get()
      .keyResults.filter((kr) => kr.okrId === okrId)
      .sort((a, b) => a.sortOrder - b.sortOrder),

  getCheckInsForKeyResult: (keyResultId) =>
    get()
      .checkIns.filter((ci) => ci.keyResultId === keyResultId)
      .sort((a, b) => new Date(b.checkedInAt).getTime() - new Date(a.checkedInAt).getTime()),

  getOKRWithKeyResults: (id) => {
    const okr = get().getOKRById(id);
    if (!okr) return undefined;
    return { ...okr, keyResults: get().getKeyResultsForOKR(id) };
  },
}));
